<template>
  <v-card flat class="pt-2 pb-4 pl-5 pr-5">
    <v-alert
      :value="!hasProducerInfo"
      type="warning">
      This company is not set up as a {{$t('booked-load.producer').toLocaleLowerCase()}}
    </v-alert>

    <v-layout align-baseline>
      <div class="title mr-3 mt-4">
        Dispatching Info
      </div>
      <v-icon @click="refresh()">refresh</v-icon>
    </v-layout>

    <v-layout :column="$vuetify.breakpoint.smAndDown" align-baseline class="mr-1 mt-4">
      <v-flex>
        <v-autocomplete
          :loading="loading && !!search"
          :items="(search && search.length > 3) ? allTruckingCompanies : []"
          :search-input.sync="search"
          v-model="selected"
          item-text="searchName"
          return-object
          class="mr-3"
          hide-no-data
          :disabled="!hasDispatchPerm"
          hide-details
          label="Search companies">

          <template slot="item" slot-scope="data">
            <v-icon
              :color="_.get(data, 'item.accountStatus.verified', null) ? 'green' : 'transparent'">
              verified_user
            </v-icon>
            <span class="ml-1">{{data.item.searchName}}</span>
          </template>
        </v-autocomplete>
       </v-flex>
      <v-flex>
        <v-btn color="accent"
               :block="$vuetify.breakpoint.smAndDown"
               :disabled="!selected && !hasDispatchPerm || loading"
               @click="sendInvite">
          Invite
        </v-btn>

        <template v-if="hideInactive">
          <v-btn @click="hideInactive = false"
                 :block="$vuetify.breakpoint.smAndDown"
                 class="mr-4" flat color="primary">
            <v-icon class="mr-2">visibility</v-icon>
            Show inactive
          </v-btn>
        </template>
        <template v-else>
          <v-btn @click="hideInactive = true"
                 :block="$vuetify.breakpoint.smAndDown"
                 class="mr-4" flat color="accent">
            <v-icon class="mr-2">visibility_off</v-icon>
            Hide inactive
          </v-btn>
        </template>

      </v-flex>
    </v-layout>

    <template v-if="members.length">
      <template v-if="$vuetify.breakpoint.smAndDown">
        <v-card v-for="member in members" :key="member.id" class="xs12 my-2">

          <v-card-title>
            <v-layout column>
              <h6 class="title">
                {{member.companyName}}
              </h6>
            </v-layout>
          </v-card-title>

          <v-card-text>
            <b>Member since:</b> {{member.memberSince | formatDate}}<br/>
            <b>Verified since:</b> {{member.verifiedSince | formatDate}}<br/>

            <v-layout row wrap>
              <v-btn small color="accent" block
                     @click="removeMember(member.companyId)">
                Remove
              </v-btn>
            </v-layout>
          </v-card-text>
        </v-card>
      </template>
      <v-data-table :headers="membersHeaders"
                    v-else
                    :pagination.sync="pagination"
                    hide-actions
                    disable-initial-sort
                    :items="members">
        <template slot="items" slot-scope="slotItems">
          <tr>
            <td>
              {{slotItems.item.companyName}}
            </td>
            <td>
              {{slotItems.item.memberSince | formatDate}}
            </td>
            <td>
              {{slotItems.item.verifiedSince | formatDate}}
            </td>
            <td>
              <v-btn flat small color="accent"
                     @click="removeMember(slotItems.item.companyId)">
                Remove
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </template>
    <div v-else>You have no Carriers in your Dispatching Network.</div>

    <v-layout align-baseline>
      <div class="title mr-3 mt-4">
        Received Invitations
      </div>
    </v-layout>

    <template v-if="received.length">
      <template v-if="$vuetify.breakpoint.smAndDown">
        <v-card v-for="receivedItem in received"
                v-if="!requestInvitationId || receivedItem.id === requestInvitationId"
                :key="receivedItem.id" class="xs12 my-2">

          <v-card-title>
            <v-layout column>
              <h6 class="title">
                {{receivedItem.host.companyName}}
              </h6>
            </v-layout>
          </v-card-title>

          <v-card-text>
            <b>Invited by:</b> {{receivedItem.invitedByDisplayName}}<br/>
            <b>Status:</b>
            <v-chip v-if="receivedItem.status === 'OFFERED'"
                    color="success" text-color="white">
              {{receivedItem.status}}
            </v-chip>
            <v-chip v-else color="primary" text-color="white">
              {{receivedItem.status}}
            </v-chip>
            <br/>

            <v-layout row wrap>
              <v-btn small color="primary"
                     block
                     :disabled="!hasDispatchPerm"
                     v-if="receivedItem.status === 'OFFERED'"
                     @click="acceptCDN(receivedItem.id)">
                Accept
              </v-btn>
              <v-btn flat small color="accent"
                     block
                     :disabled="!hasDispatchPerm"
                     v-if="receivedItem.status === 'OFFERED'"
                     @click="removeCDN(receivedItem.id)">
                Refuse
              </v-btn>
            </v-layout>
          </v-card-text>
        </v-card>
      </template>
      <v-data-table v-else
                    :headers="headers"
                    :pagination.sync="pagination"
                    disable-initial-sort
                    hide-actions
                    :items="received">
        <template slot="items" slot-scope="slotItems">
          <tr :bgcolor="slotItems.item.id === requestInvitationId ? '#88a2aa' : null" :class="{ 'selected-invitation':  slotItems.item.id === requestInvitationId}">
            <td>
              {{slotItems.item.host.companyName}}
            </td>
            <td>
              {{slotItems.item.invitedByDisplayName}}
            </td>
            <td>
              <v-chip v-if="slotItems.item.status === 'OFFERED'"
                      color="success" text-color="white">
                {{slotItems.item.status}}
              </v-chip>
              <v-chip v-else color="primary" text-color="white">
                {{slotItems.item.status}}
              </v-chip>
            </td>
            <td>
              <v-btn small :color="slotItems.item.id === requestInvitationId ? 'accent' : 'primary'"
                     :disabled="!hasDispatchPerm"
                     v-if="slotItems.item.status === 'OFFERED'"
                     @click="acceptCDN(slotItems.item.id)">
                Accept
              </v-btn>
              <v-btn flat small color="accent"
                     :disabled="!hasDispatchPerm"
                     v-if="slotItems.item.status === 'OFFERED'"
                     @click="removeCDN(slotItems.item.id)">
                Refuse
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </template>
    <div v-else>No invitations received to join other CDNs at this time.</div>

    <v-layout align-baseline>
      <div class="title mr-3 mt-4">
        Offered Invitations
      </div>
    </v-layout>

    <template v-if="offered.length">
      <template v-if="$vuetify.breakpoint.smAndDown">
        <v-card v-for="offeredItem in offered" :key="offeredItem.id" class="xs12 my-2">

          <v-card-title>
            <v-layout column>
              <h6 class="title">
                {{offeredItem.invited.companyName}}
              </h6>
            </v-layout>
          </v-card-title>

          <v-card-text>
            <b>Invited by:</b> {{offeredItem.invitedByDisplayName}}<br/>
            <b>Status:</b>
            <v-chip v-if="offeredItem.status === 'OFFERED'"
                    color="success" text-color="white">
              {{offeredItem.status}}
            </v-chip>
            <v-chip v-else color="primary" text-color="white">
              {{offeredItem.status}}
            </v-chip>
            <br/>

            <v-layout row wrap>
              <v-btn color="accent"
                     block
                     :disabled="!hasDispatchPerm"
                     v-if="offeredItem.status === 'OFFERED'"
                     @click="removeCDN(offeredItem.id)">
                Remove
              </v-btn>
            </v-layout>
          </v-card-text>
        </v-card>
      </template>
      <v-data-table :headers="headers"
                      v-else
                      :pagination.sync="pagination"
                      hide-actions
                      disable-initial-sort
                      :items="offered">
          <template slot="items" slot-scope="slotItems">
            <tr>
              <td>
                {{slotItems.item.invited.companyName}}
              </td>
              <td>
                {{slotItems.item.invitedByDisplayName}}
              </td>
              <td>
                <v-chip v-if="slotItems.item.status === 'OFFERED'"
                        color="success" text-color="white">
                  {{slotItems.item.status}}
                </v-chip>
                <v-chip v-else color="primary" text-color="white">
                  {{slotItems.item.status}}
                </v-chip>
              </td>
              <td>
                <v-btn small color="accent" flat
                       :disabled="!hasDispatchPerm"
                       v-if="slotItems.item.status === 'OFFERED'"
                       @click="removeCDN(slotItems.item.id)">
                  Remove
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table >
    </template>
    <div v-else>No invitations to join your CDN at this time.</div>
  </v-card>
</template>

<script>
import _ from 'lodash';
import API from '@/api';
import { mapGetters } from 'vuex';
import * as types from '@/store/mutationTypes';
import { accountHasOfficePermission } from '@/store/permissions';
import {
  hasProducerInfo,
} from '@/types/models/company';

export default {
  name: 'DispatchingInfoSection',
  props: {
    company: {
      type: Object,
    },
  },
  data() {
    return {
      hideInactive: true,
      loading: false,
      select: null,
      selected: '',
      search: '',
      cdns: [],
      pagination: {
        sortBy: 'created',
        descending: true,
        rowsPerPage: -1, // -1 for All
      },
      membersHeaders: [
        { text: 'Company Name', value: 'companyName' },
        { text: 'Member since', value: 'memberSince' },
        { text: 'Verified since', value: 'verifiedSince' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      headers: [
        { text: 'Company Name', value: 'companyName' },
        { text: 'Invited by', value: 'companyName' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    _() {
      return _;
    },
    ...mapGetters('companies', {
      allTruckingCompanies: 'allTruckingCompanies',
    }),
    ...mapGetters('profiles', {
      isLtnAdmin: 'isLtnAdmin',
    }),
    hasDispatchPerm() {
      const profileCompany = this.findCompanyAccount()(this.company.id);
      return this.isLtnAdmin || accountHasOfficePermission(profileCompany);
    },
    hasProducerInfo() {
      return hasProducerInfo(this.company);
    },
    offered() {
      return _.filter(_.get(this.cdns, 'offered', []), cdn => (this.hideInactive && cdn.status === 'OFFERED') || !this.hideInactive);
    },
    received() {
      return _.filter(_.get(this.cdns, 'received', []), cdn => (this.hideInactive && cdn.status === 'OFFERED') || !this.hideInactive);
    },
    members() {
      return _.get(this.cdns, 'members', []);
    },
    requestInvitationId() {
      return this.$route.query.inviteId;
    },
    requestedInvitation() {
      return _.chain(this.received)
        .filter(r => this.requestInvitationId)
        .find(r => r.id === this.requestInvitationId)
        .value();
    },
  },
  watch: {
    company() {
      this.loadCDNs();
    },
  },
  methods: {
    ...mapGetters('profiles', [
      'findCompanyAccount',
    ]),
    async acceptCDN(inviteId) {
      try {
        this.$store.commit(`applications/${types.START_LOADING}`);
        await API.dispatchers.acceptInvitation(inviteId);

        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Accepted',
          timeout: 7000,
        });

        this.loadCDNs();
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
    async removeCDN(inviteId) {
      try {
        this.$store.commit(`applications/${types.START_LOADING}`);
        await API.dispatchers.refuseInvitation(inviteId);

        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Removed',
          timeout: 7000,
        });

        this.loadCDNs();
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
    async removeMember(companyId) {
      try {
        this.$store.commit(`applications/${types.START_LOADING}`);
        await API.dispatchers.removeMember({
          companyId: this.company.id,
          memberCompanyId: companyId,
        });

        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Removed',
          timeout: 7000,
        });

        this.loadCDNs();
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
    async sendInvite() {
      try {
        this.$store.commit(`applications/${types.START_LOADING}`);
        await API.dispatchers.inviteCompany({
          companyId: this.company.id,
          invitedCompanyId: this.selected.id,
        });

        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Invitation sent',
          timeout: 7000,
        });

        this.selected = null;
        this.loadCDNs();
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
    async loadCDNs() {
      try {
        this.$store.commit(`applications/${types.START_LOADING}`);
        const apiResponse = await API.dispatchers.getCompanyCDNs({
          companyId: this.company.id,
        });

        this.cdns = apiResponse.data;
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
    async loadCompanies() {
      this.loading = true;
      await this.$store.dispatch('companies/getAllCompanies');
      this.loading = false;
    },
    refresh() {
      this.loadCompanies();
      this.loadCDNs();
    },
  },
  created() {
    this.refresh();
  },
};
</script>

<style scoped>
.selected-invitation {
  color: white;
}

</style>



// WEBPACK FOOTER //
// src/pages/Companies/Sections/DispatchingInfoSection.vue